import { addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useFormik } from 'formik';
import moment from 'moment';
import React, { useState } from 'react';
import { Button, Textarea } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import { DatePicker } from '../../../components/forms/DatePicker';
import ModalForm from '../../../components/forms/ModalForm';
import { PickupService } from '../../../services/pickup/pickupService';
import useHandleErrors from '../../../hooks/useHandleErrors';

interface ReschedulePickupModalProps {
	isOpen: boolean;
	onClose: () => void;
	ids: string[];
}

/**
 * FORMATOS DE FECHA UTILIZADOS:
 * - Frontend Display: DD/MM/YY HH:MM:SS (formato mostrado al usuario)
 * - Backend API: Y-m-d H:i:s (formato enviado al backend - PHP DateTime)
 * - Internal Storage: YYYY-MM-DD HH:mm:ss (formato interno de moment.js)
 */

// Constantes para formatos de fecha
const DATE_FORMATS = {
	DISPLAY: 'DD/MM/YY HH:mm:ss',        // Formato mostrado al usuario
	BACKEND: 'YYYY-MM-DD HH:mm:ss',      // Formato para envío al backend
	INTERNAL: 'YYYY-MM-DD HH:mm:ss',     // Formato interno
	VALIDATION: 'YYYY-MM-DD HH:mm:ss'    // Formato para validación
} as const;

/**
 * Convierte una fecha del formato interno al formato de display
 * @param dateString - Fecha en formato interno (YYYY-MM-DD HH:mm:ss)
 * @returns Fecha en formato display (DD/MM/YY HH:MM:SS)
 */
const formatDateForDisplay = (dateString: string): string => {
	if (!dateString) return '';
	const momentDate = moment(dateString, DATE_FORMATS.INTERNAL, true);
	return momentDate.isValid() ? momentDate.format(DATE_FORMATS.DISPLAY) : '';
};

/**
 * Convierte una fecha del formato interno al formato del backend
 * @param dateString - Fecha en formato interno (YYYY-MM-DD HH:mm:ss)
 * @returns Fecha en formato backend (Y-m-d H:i:s)
 */
const formatDateForBackend = (dateString: string): string => {
	if (!dateString) return '';
	const momentDate = moment(dateString, DATE_FORMATS.INTERNAL, true);
	return momentDate.isValid() ? momentDate.format(DATE_FORMATS.BACKEND) : '';
};

/**
 * Valida que una fecha esté en el formato correcto y sea posterior al día actual
 * @param dateString - Fecha a validar
 * @returns true si la fecha es válida y posterior a hoy
 */
const validateDateFormat = (dateString: string): boolean => {
	if (!dateString) return false;
	const momentDate = moment(dateString, DATE_FORMATS.VALIDATION, true);
	return momentDate.isValid() && momentDate.isAfter(moment(), 'day');
};

const validationSchema = yup.object({
    newDate: yup
        .string()
        .required('La nueva fecha de recogida es obligatoria')
        .test('after-today', 'La nueva fecha debe ser posterior al día actual', (val) => {
            if (!val) return false;
            return validateDateFormat(val);
        })
        .test('valid-format', 'El formato de fecha no es válido', (val) => {
            if (!val) return false;
            const momentDate = moment(val, DATE_FORMATS.VALIDATION, true);
            return momentDate.isValid();
        }),
    rescheduleReason: yup.string().trim().max(1000, 'El motivo no puede exceder los 1000 caracteres').notRequired(),
});

const ReschedulePickupModal: React.FC<ReschedulePickupModalProps> = ({ isOpen, onClose, ids }) => {
	// STATES
	const [loading, setLoading] = useState(false);

	// SERVICES
	const pickupService = new PickupService();
	const { handleErrors } = useHandleErrors();

	// FORM
	const formik = useFormik({
		initialValues: {
			newDate: '',
			rescheduleReason: '',
		},
		validationSchema,
		onSubmit: async (values) => handleSubmit(values),
	});

	// FUNCTIONS

	//-------------------------------------------------------------------------------------------------------------
	/**
	 * @ES REPROGRAMAR RECOGIDA MANUALMENTE
	 * @EN MANUALLY RESCHEDULE PICKUPS
	 */
	//-------------------------------------------------------------------------------------------------------------
	const handleSubmit = async (values: any) => {
		setLoading(true);
		
		// Validar formato antes del envío
		if (!validateDateFormat(values.newDate)) {
			toast.error('El formato de fecha no es válido');
			setLoading(false);
			return;
		}

		// Convertir fecha al formato del backend
		const backendFormattedDate = formatDateForBackend(values.newDate);
		
		if (!backendFormattedDate) {
			toast.error('Error al procesar la fecha seleccionada');
			setLoading(false);
			return;
		}

		const payload = {
			ids: ids || [],
			newDate: backendFormattedDate, // Fecha en formato Y-m-d H:i:s para el backend
			rescheduleReason: values.rescheduleReason || null,
		};


		try {
			const response = (await pickupService.reschedulePickups(payload)).getResponseData();
			if(response?.success){
				toast.success('La/s recogida/s ha/n sido reprogramada/s con éxito');
			}else{
				handleErrors(response);
			}
			setLoading(false);
			handleClose();
		} catch (error: any) {
			toast.error(error?.message || 'Error al reprogramar la recogida');
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		formik.resetForm()
		onClose();
	};

	/**
	 * Maneja el cambio de fecha del DatePicker
	 * @param date - Fecha seleccionada del DatePicker
	 */
	const handleDateChange = (date: Date | null) => {
		if (date) {
			// Convertir a formato interno para almacenamiento
			const internalFormat = moment(date).format(DATE_FORMATS.INTERNAL);
			formik.setFieldValue('newDate', internalFormat);
		} else {
			formik.setFieldValue('newDate', '');
		}
	};

	/**
	 * Obtiene la fecha seleccionada para mostrar en el DatePicker
	 * @returns Date object o null
	 */
	const getSelectedDate = (): Date | null => {
		if (!formik.values.newDate) return null;
		const momentDate = moment(formik.values.newDate, DATE_FORMATS.INTERNAL, true);
		return momentDate.isValid() ? momentDate.toDate() : null;
	};

	return (
		<ModalForm isOpen={isOpen} onClose={handleClose} title={`Reprogramar ${ids.length > 1 ? ids.length : ''} Recogida/s  `}>
			<form onSubmit={formik.handleSubmit} className="space-y-6">
				{/* Selector de fecha y hora */}
				<div>
					<label htmlFor="newDate" className="block text-sm font-medium text-gray-900 mb-2">
						Programar próxima recogida
					</label>
					<DatePicker
						dateFormat="dd/MM/yy HH:mm:ss"
						timeFormat="HH:mm:ss"
						timeCaption="Hora"
						showTimeSelect
						timeIntervals={15}
						minDate={addDays(new Date(), 1)}
						inputProps={{
							inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.newDate ? 'border-red-500' : ''}`,
						}}
						className="w-full"
						selected={getSelectedDate()}
						onChange={handleDateChange}
						locale={es}
						placeholderText="Seleccionar fecha y hora (DD/MM/YY HH:MM:SS)"
					/>
					{formik.errors.newDate ? (
						<span className="text-xs text-red-600">{String(formik.errors.newDate)}</span>
					) : null}
					
					<p className="mt-2 text-xs text-amber-600">
						Nota: La fecha seleccionada podría no corresponder a la ruta habitual. El sistema podría asignar esta recogida a una ruta diferente.
					</p>
				</div>

				{/* Motivo (opcional) */}
				<div>
					<label htmlFor="rescheduleReason" className="block text-sm font-medium text-gray-900 mb-2">
						Motivo (opcional)
					</label>
					<Textarea
						id="rescheduleReason"
						placeholder="Describe el motivo de la reprogramación..."
						rows={3}
						maxLength={1000}
						{...formik.getFieldProps('rescheduleReason')}
					/>
				</div>

				{/* Botones */}
				<div className="flex justify-end gap-3 py-4 border-t">
					<Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
						Cancelar
					</Button>
					<Button type="submit" isLoading={loading} disabled={!formik.values.newDate || loading}>
						Reprogramar Recogida
					</Button>
				</div>
			</form>
		</ModalForm>
	);
};

export default ReschedulePickupModal;